import { Link } from 'react-router-dom'

import type { PieceOrigin } from '@/components/piece/PieceReference'
import { SubjectStake } from '@/components/recovery/SubjectStake'
import { ProductMediaGallery } from '@/components/warehouse/ProductMediaGallery'
import { SectionCard } from '@/components/warehouse/primitives'
import { ABSENT_FACT, subjectStatusLabels } from '@/lib/recovery/labels'
import type { LegacyProduct } from '@/lib/recovery/legacy-product'

export const RECOVERY_QUEUE_HREF = '/inventory/recovery'

function stateSentence(product: LegacyProduct): string {
  if (product.subject !== null) {
    return 'Это строка старого каталога: она ещё не сопоставлена с изделием из 1С и ждёт решения в очереди сопоставления.'
  }
  return product.queueError === null
    ? 'Это строка каталога, не сопоставленная с изделием из 1С; записи в очереди сопоставления для неё нет.'
    : 'Это строка каталога, не сопоставленная с изделием из 1С; очередь сопоставления сейчас не ответила.'
}

/** The card for a product that exists outside the piece population. */
export function LegacyProductCard({
  product,
  origin,
  onBack,
}: {
  product: LegacyProduct
  origin: PieceOrigin | null
  onBack: () => void
}) {
  const { media, subject } = product
  const title = subject?.originalProduct.title ?? subject?.title ?? null
  const article = subject?.originalProduct.article ?? subject?.article ?? null

  return (
    <div className="h-full overflow-y-auto bg-ivory" data-testid="legacy-product-card" data-product-id={product.productId} data-subject-status={subject?.status}>
      <div className="mx-auto max-w-[1200px] px-3 py-4 pb-[calc(32px+env(safe-area-inset-bottom))] sm:px-5 md:px-7 md:py-6">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {origin
            ? <Link to={origin.href} data-testid="piece-card-origin" className="rounded-md border border-rule bg-paper px-2.5 py-1.5 text-ink-2 hover:bg-bone/60 hover:text-ink">{origin.label ? `Назад к ${origin.label}` : 'Назад'}</Link>
            : <button type="button" onClick={onBack} className="rounded-md border border-rule bg-paper px-2.5 py-1.5 text-ink-2 hover:bg-bone/60 hover:text-ink">Назад</button>}
          <Link to="/inventory" className="rounded-md px-2 py-1.5 hover:text-ink">Склад</Link>
          <span aria-hidden="true">/</span>
          <span className="truncate text-ink-2">{article ?? title ?? ABSENT_FACT}</span>
        </nav>

        <header className="mt-3 border-b border-rule pb-4">
          <p className="text-xs text-ink-muted">Товар без изделия 1С</p>
          <h1 className="mt-1 text-[22px] font-semibold tracking-tight text-ink md:text-[26px]">{title ?? ABSENT_FACT}</h1>
          <p className="mt-1 text-[11px] text-ink-muted">
            Артикул <span className="font-mono" data-fact="article">{article ?? ABSENT_FACT}</span>
            {' · '}Идентификатор товара <span className="font-mono">{product.productId}</span>
          </p>
        </header>

        <div role="status" data-testid="legacy-product-state" className="mt-4 rounded-md border border-gold/40 bg-gold/5 px-3 py-2 text-xs text-ink-2">
          <p>{stateSentence(product)}</p>
          {subject === null ? null : <p className="mt-1 text-ink-muted">Состояние в очереди: {subjectStatusLabels[subject.status]}</p>}
          {product.queueError === null ? null : <p className="mt-1 text-ink-muted">{product.queueError}</p>}
          <Link to={RECOVERY_QUEUE_HREF} data-testid="legacy-product-queue-link" className="mt-2 inline-block rounded-md border border-rule bg-paper px-3 py-1.5 text-xs text-ink-2 hover:bg-bone/60">Открыть очередь сопоставления</Link>
        </div>

        <div className="mt-4 space-y-3">
          <SectionCard title={`Медиа · ${media.merchandisingTotal}`} ariaLabel="Фото и видео" subtitle="Фото и видео этой строки каталога">
            <ProductMediaGallery media={media} columns={6} heading={title ?? undefined} />
          </SectionCard>

          <SectionCard title="Заказы и продажи" subtitle="Что привязано к этой строке и перейдёт к изделию после сопоставления.">
            {subject === null
              ? <p className="text-xs text-ink-muted" data-testid="legacy-product-stake-unavailable">Заказы и продажи этой строки читаются из очереди сопоставления; без записи в очереди показать их нельзя.</p>
              : <SubjectStake counts={subject.attachmentCounts} attached={subject.attached} />}
          </SectionCard>
        </div>
      </div>
    </div>
  )
}
